import { skyness } from '../../lib/nightGrade'
/**
 * What kind of photograph is this?
 *
 * Read ONCE when the source photo changes, never while a slider moves. The
 * numbers are cheap but they are a full pass over the pixels, and doing that
 * per frame is exactly the CPU work the port exists to remove.
 *
 * Percentiles rather than a mean. A mean is dragged around by a dark roof or a
 * lot of vegetation and says very little about whether there is sun in the
 * picture; P90 and P95 answer that directly. If the top of the range is jammed
 * against white there are daylight highlights to collapse, and the grade
 * should lean harder. If the photo arrived already dim - overcast, or close to
 * sunset - the same preset would crush it, so it should back off.
 */

export interface SceneStats {
  mean: number
  p50: number
  p75: number
  p90: number
  p95: number
  /** Fraction of the frame that is very close to blown out. */
  hot: number
  /**
   * Median luminance of everything that is NOT sky.
   *
   * The one that matters for readability, and the one the adaptation was
   * missing. A photograph can have a perfectly bright frame and a dark house:
   * on the benchmark set the pale house measures P50 0.502 and P95 0.831 for
   * the whole frame - which reads as an ordinary daylight shot and earned a
   * scene compensation of -0.02, essentially none - while its actual property
   * sits at 0.354. The sky was doing the talking. It came out as the darkest
   * result in the set.
   */
  propP50: number
}

const SAMPLE_MAX = 480

export function computeSceneStats(source: HTMLImageElement | HTMLCanvasElement): SceneStats {
  const sw = 'naturalWidth' in source ? source.naturalWidth : source.width
  const sh = 'naturalHeight' in source ? source.naturalHeight : source.height
  const scale = Math.min(1, SAMPLE_MAX / Math.max(1, sw))
  const w = Math.max(1, Math.round(sw * scale))
  const h = Math.max(1, Math.round(sh * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(source, 0, 0, w, h)
  const { data } = ctx.getImageData(0, 0, w, h)

  // A 256 bin histogram is enough for percentiles and avoids sorting a
  // million floats every time somebody drops a photo on the page.
  const hist = new Uint32Array(256)
  const propHist = new Uint32Array(256)
  let propN = 0
  let sum = 0
  const n = w * h
  for (let i = 0, px = 0; i < data.length; i += 4, px++) {
    const y = data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722
    const b = Math.min(255, Math.max(0, Math.round(y)))
    hist[b]++
    sum += b
    // Same sky test the mask texture uses, so the two never disagree.
    if (skyness(data[i], data[i + 1], data[i + 2], Math.floor(px / w) / h) < 0.5) {
      propHist[b]++
      propN++
    }
  }

  const median = (h32: Uint32Array, total: number): number => {
    const target = 0.5 * total
    let seen = 0
    for (let b = 0; b < 256; b++) {
      seen += h32[b]
      if (seen >= target) return b / 255
    }
    return 1
  }
  const at = (frac: number): number => {
    const target = frac * n
    let seen = 0
    for (let b = 0; b < 256; b++) {
      seen += hist[b]
      if (seen >= target) return b / 255
    }
    return 1
  }

  let hot = 0
  for (let b = 220; b < 256; b++) hot += hist[b]

  return {
    mean: sum / n / 255,
    p50: at(0.5),
    p75: at(0.75),
    p90: at(0.9),
    p95: at(0.95),
    hot: hot / n,
    propP50: propN > n * 0.05 ? median(propHist, propN) : median(hist, n),
  }
}

/**
 * How hard to lean on the preset for THIS photo, -1 to +1.
 *
 *   +1  bright midday with blown highlights: lean harder
 *    0  an ordinary, well exposed daytime photo: the preset as written
 *   -1  already dim, overcast or near sunset: back off or we crush it
 *
 * Clamped hard on purpose. This is a nudge that keeps the feature reliable
 * across whatever a contractor photographs in a driveway, not a second grade
 * layered on top of the first.
 */
export function sceneCompensation(s: SceneStats): number {
  const bright = (s.p95 - 0.8) / 0.18 + (s.hot - 0.06) / 0.14
  return Math.max(-1, Math.min(1, bright * 0.5))
}

/**
 * Apply the compensation to the two numbers that define the curve.
 *
 * The clamps are wide on purpose. An earlier pair of them looked harmless and
 * was not: a shadow floor sitting at 0.55 pinned every preset deeper than dusk
 * to the same value, so the picture stopped getting darker no matter what the
 * preset asked for - a sweep of five settings produced three identical frames.
 * Clamps belong at the edge of what is physically sane, never in the middle of
 * the working range.
 */
export function adaptCurve(
  shadowKeep: number,
  highlightKeep: number,
  comp: number,
): { shadowKeep: number; highlightKeep: number } {
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
  return {
    highlightKeep: clamp(highlightKeep * (1 - 0.3 * comp), 0.01, 0.8),
    shadowKeep: clamp(shadowKeep * (1 - 0.06 * comp), 0.12, 1),
  }
}


/**
 * How much of the grade this photograph does NOT need, 0 to 1.
 *
 * A shot taken at dusk, or one the tool has already graded, is close to where
 * Night is trying to get to. Applying the full treatment to it drives it past
 * the target and into mud - measured on our own night export, the default
 * preset took 48% of the frame below the readable threshold and dropped the
 * property median to 20.
 *
 * Two conditions, and BOTH have to hold, because either one alone gets it
 * wrong. A low median says the picture is dark - but a sunlit house under a
 * heavy tree canopy has a low median too, and it very much still needs the
 * grade. What separates them is the top of the range: daylight leaves blown
 * highlights behind, and evening does not. On the benchmark set the blue hour
 * and sunset references sit at P95 0.64 and 0.57 with no hot pixels, while the
 * shaded canopy house sits at 0.85 with 4.5% - same median, opposite answer.
 *
 * So: dark AND no daylight highlights left. Nothing here looks at the clock or
 * the file name, only at what the photograph actually contains.
 */
export function alreadyGraded(s: SceneStats): number {
  const smoothstep = (e0: number, e1: number, x: number) => {
    const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)))
    return t * t * (3 - 2 * t)
  }
  // FITTED TO THE BENCHMARK SET, NOT CHOSEN BY EYE.
  //
  // The first pair of numbers here was picked by looking at a table, which is
  // the same guessing this whole process exists to replace. These come from a
  // least squares fit across the thirteen photographs against what each one
  // SHOULD do - our own night export and a real sunset frame barely moving, a
  // real blue hour frame backing off most of the way, a sunlit house under a
  // canopy still getting the full treatment - with a minimum window width
  // imposed so the fit cannot collapse into a cliff.
  //
  // The width matters. An earlier fit that maximised the separation margin
  // scored perfectly and was useless: it turned this into a hard switch and
  // threw the genuine blue hour photograph onto the daytime side of it.
  const dark = smoothstep(0.51, 0.29, s.p50)
  const noHighlights = smoothstep(0.9, 0.71, s.p95)
  return dark * noHighlights
}


/**
 * How much extra to lift a photograph whose PROPERTY arrived dark, 0 to 1.
 *
 * Separate from sceneCompensation on purpose. That one asks "is there daylight
 * to collapse", which is a question about the whole frame and is correctly
 * answered by P95 and the hot fraction. This one asks "will the house be
 * readable", which is a question about the house, and the two come apart: a
 * bright sky over a shaded property scores as an ordinary daylight shot and
 * gets no help, which is exactly how the pale house ended up the darkest
 * result in the set at a property median of 33 while everything else sat in
 * the high forties and fifties.
 *
 * Multiplied by how much of the grade the photo still needs, so a frame that
 * is dark because it was taken at dusk is not lifted back out of dusk - it is
 * already where it should be, and alreadyGraded is what knows that.
 */
export function darkSourceLift(s: SceneStats, alreadyGradedAmount: number): number {
  const smoothstep = (e0: number, e1: number, x: number) => {
    const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)))
    return t * t * (3 - 2 * t)
  }
  return smoothstep(0.5, 0.32, s.propP50) * (1 - alreadyGradedAmount)
}
